/* eslint-disable react/jsx-no-bind -- one-line forwarding to non-memo'd children; component-anatomy.md lists this as a narrow exception */
import PauseIcon from '@mui/icons-material/Pause'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import videojs from 'video.js'

import 'video.js/dist/video-js.css'

import { useLazyGetVisionVideoArtifactsQuery } from 'api/route/getVisionVideoArtifacts'
import T from 'T'
import { DIVIDER } from 'theme/colors'
import { handleError } from 'utils/error'
import { getVisionVideoDownloadUrl, resolveVisionGsUri } from 'utils/file'
import { get } from 'utils/lodash'

import type Player from 'video.js/dist/types/player'

export interface VisionVideoArtifact {
  id?: string | number
  filePath?: string
  [key: string]: unknown
}

export interface VisionVideoPlayerHandle {
  play: () => void
  pause: () => void
  seekTo: (time: number) => void
  getCurrentTime: () => number
  getDuration: () => number
  getActiveCameraIndex: () => number
  // Rejects when the canvas is tainted; resolves null before the first decoded frame.
  captureFrame: () => Promise<Blob | null>
}

export interface VisionVideoPlayerProps {
  workOrderId: string
  /**
   * Direct media uri (http or gs://). When set, skips the work-order artifacts
   * fetch and plays this single source — used by vision-exceptions detection
   * media. gs:// is resolved via Firebase getDownloadURL.
   */
  videoUri?: string
  variant?: 'embedded' | 'overlay'
  // video.js's own bar; off by default so surfaces with their own controls don't double up.
  showNativeControls?: boolean
  showCameraTabs?: boolean
  showCenterPlayPauseIcon?: boolean
  onTimeUpdate?: (currentTime: number) => void
  onDurationChange?: (duration: number) => void
  onPlayingChange?: (isPlaying: boolean) => void
  onError?: () => void
  sx?: object
}

const placeholderBoxSx = (variant: 'embedded' | 'overlay', sx?: object): object => ({
  ...sx,
  ...(variant === 'embedded' ? { width: '100%', height: '100%' } : { aspectRatio: '16/9' }),
  bgcolor: 'grey.100',
  borderRadius: variant === 'embedded' ? 0 : 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const CAPTURE_MIME = 'image/jpeg'
const CAPTURE_QUALITY = 0.92

const getVideoElement = (player: Player | null): HTMLVideoElement | null => {
  if (!player || player.isDisposed()) return null
  return player.el()?.querySelector('video') ?? null
}

const disposePlayer = (player: Player | null): void => {
  if (!player) return
  try {
    if (!player.isDisposed()) {
      player.dispose()
    }
  } catch {
    // ignore disposal races
  }
}

const VisionVideoPlayer = forwardRef<VisionVideoPlayerHandle, VisionVideoPlayerProps>(function VisionVideoPlayer(
  {
    workOrderId,
    videoUri,
    variant = 'embedded',
    showNativeControls = false,
    showCameraTabs = true,
    showCenterPlayPauseIcon = true,
    onTimeUpdate,
    onDurationChange,
    onPlayingChange,
    onError,
    sx,
  },
  ref
) {
  const videoRef = useRef<HTMLDivElement | null>(null)
  const playerRef = useRef<Player | null>(null)

  const [isPlaying, setIsPlaying] = useState(false)
  const [activeCamera, setActiveCamera] = useState(0)
  const [videoSrc, setVideoSrc] = useState('')
  const [artifacts, setArtifacts] = useState<VisionVideoArtifact[]>([])
  const [hasError, setHasError] = useState(false)

  const [getVideoArtifacts] = useLazyGetVisionVideoArtifactsQuery()

  // Hold the latest callbacks in a ref so the fetch/player effects don't list
  // them as deps. Consumers pass inline callbacks (e.g. onError={() => ...}),
  // whose identity changes on every parent re-render; player events feed state
  // back up to the parent, so depending on the callbacks would re-run the
  // effect every frame — disposing the player and re-fetching artifacts in a
  // loop.
  const callbacksRef = useRef({ onTimeUpdate, onDurationChange, onPlayingChange, onError })
  callbacksRef.current = { onTimeUpdate, onDurationChange, onPlayingChange, onError }

  useImperativeHandle(
    ref,
    () => ({
      play: () => {
        playerRef.current?.play()
      },
      pause: () => {
        playerRef.current?.pause()
      },
      seekTo: (time: number) => {
        if (playerRef.current && Number.isFinite(time)) {
          playerRef.current.currentTime(time)
        }
      },
      getCurrentTime: () => playerRef.current?.currentTime() ?? 0,
      getDuration: () => playerRef.current?.duration() ?? 0,
      getActiveCameraIndex: () => activeCamera,
      captureFrame: async () => {
        const videoElement = getVideoElement(playerRef.current)
        if (!videoElement?.videoWidth) return null
        const canvas = document.createElement('canvas')
        canvas.width = videoElement.videoWidth
        canvas.height = videoElement.videoHeight
        canvas.getContext('2d')?.drawImage(videoElement, 0, 0, canvas.width, canvas.height)
        return new Promise<Blob | null>(resolve => canvas.toBlob(resolve, CAPTURE_MIME, CAPTURE_QUALITY))
      },
    }),
    [activeCamera]
  )

  useEffect(() => {
    // Direct-uri mode (vision-exceptions): resolve the detection's media VIDEO
    // uri (http passthrough or gs:// via Firebase) and skip the artifacts fetch.
    if (videoUri) {
      let cancelled = false
      disposePlayer(playerRef.current)
      playerRef.current = null
      setVideoSrc('')
      setIsPlaying(false)
      setArtifacts([{ id: 'direct' }])
      setActiveCamera(0)
      setHasError(false)
      const resolve = videoUri.startsWith('http') ? Promise.resolve(videoUri) : resolveVisionGsUri(videoUri)
      resolve
        .then(url => {
          if (cancelled) return
          if (url) setVideoSrc(url)
          else {
            setHasError(true)
            callbacksRef.current.onError?.()
          }
        })
        .catch(err => {
          handleError(err)
          if (!cancelled) {
            setHasError(true)
            callbacksRef.current.onError?.()
          }
        })
      return () => {
        cancelled = true
      }
    }
    if (!workOrderId) return undefined
    let isCancelled = false
    disposePlayer(playerRef.current)
    playerRef.current = null
    setVideoSrc('')
    setIsPlaying(false)
    setArtifacts([])
    setActiveCamera(0)
    setHasError(false)
    getVideoArtifacts({ workOrderId })
      .unwrap()
      .then(async data => {
        if (isCancelled) return
        const fetchedArtifacts: VisionVideoArtifact[] = Array.isArray(get(data, 'artifacts')) ? data.artifacts : []
        setArtifacts(fetchedArtifacts)
        setActiveCamera(0)
        const filePath = fetchedArtifacts[0]?.filePath ?? ''
        if (!filePath) {
          setHasError(true)
          callbacksRef.current.onError?.()
          return
        }
        try {
          const url = await getVisionVideoDownloadUrl(filePath)
          if (isCancelled) return
          if (url) setVideoSrc(url)
          else {
            setHasError(true)
            callbacksRef.current.onError?.()
          }
        } catch (err) {
          handleError(err)
          if (!isCancelled) {
            setHasError(true)
            callbacksRef.current.onError?.()
          }
        }
      })
      .catch(err => {
        handleError(err)
        if (!isCancelled) {
          setHasError(true)
          callbacksRef.current.onError?.()
        }
      })
    return () => {
      isCancelled = true
    }
  }, [videoUri, workOrderId, getVideoArtifacts])

  useEffect(() => {
    if (!videoSrc || !videoRef.current) {
      disposePlayer(playerRef.current)
      playerRef.current = null
      return undefined
    }
    if (playerRef.current) {
      return undefined
    }
    const videoElement = document.createElement('video-js')
    videoElement.classList.add('vjs-big-play-centered')
    // Untainted canvas for captureFrame; Firebase Storage answers `access-control-allow-origin: *`.
    videoElement.setAttribute('crossorigin', 'anonymous')
    videoRef.current.appendChild(videoElement)
    const player = videojs(
      videoElement,
      {
        controls: showNativeControls,
        autoplay: true,
        preload: 'auto',
        // Embedded (detail panel) fills its fixed-height column; overlay
        // keeps the width-driven 16:9 box.
        fluid: variant !== 'embedded',
        fill: variant === 'embedded',
        responsive: true,
        sources: [{ src: videoSrc, type: 'video/mp4' }],
      },
      () => {
        callbacksRef.current.onDurationChange?.(player.duration() ?? 0)
        player.on('timeupdate', () => {
          callbacksRef.current.onTimeUpdate?.(player.currentTime() ?? 0)
        })
        player.on('loadedmetadata', () => {
          callbacksRef.current.onDurationChange?.(player.duration() ?? 0)
        })
        player.on('ended', () => {
          setIsPlaying(false)
          callbacksRef.current.onPlayingChange?.(false)
        })
        player.on('play', () => {
          setIsPlaying(true)
          callbacksRef.current.onPlayingChange?.(true)
        })
        player.on('pause', () => {
          setIsPlaying(false)
          callbacksRef.current.onPlayingChange?.(false)
        })
        player.on('error', () => {
          setHasError(true)
          callbacksRef.current.onError?.()
        })
      }
    )
    playerRef.current = player
    return () => {
      disposePlayer(playerRef.current)
      playerRef.current = null
    }
  }, [videoSrc, variant, showNativeControls])

  const handleCameraChange = async (index: number): Promise<void> => {
    const filePath = artifacts[index]?.filePath ?? ''
    if (!filePath) return
    setActiveCamera(index)
    setIsPlaying(false)
    onPlayingChange?.(false)
    disposePlayer(playerRef.current)
    playerRef.current = null
    try {
      const url = await getVisionVideoDownloadUrl(filePath)
      setVideoSrc(url ?? '')
    } catch (err) {
      handleError(err)
      setVideoSrc('')
    }
  }

  const handlePlayPauseClick = (): void => {
    if (!playerRef.current) return
    if (isPlaying) playerRef.current.pause()
    else playerRef.current.play()
  }

  const cameraTabClickHandlers = useMemo(
    () => artifacts.map((_artifact, idx) => () => handleCameraChange(idx)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [artifacts]
  )

  if (hasError) {
    return (
      <Box sx={placeholderBoxSx(variant, sx)}>
        <Typography color="text.secondary" variant="body2">
          {T.FAILED_TO_LOAD_VIDEO}
        </Typography>
      </Box>
    )
  }

  if (artifacts.length === 0 && !hasError) {
    return (
      <Box sx={placeholderBoxSx(variant, sx)}>
        <Typography color="text.secondary" variant="body2">
          {T.LOADING}
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', ...sx }}>
      <Box
        ref={videoRef}
        sx={{
          width: '100%',
          ...(variant === 'embedded' ? { flex: 1, minHeight: 0 } : { aspectRatio: '16/9' }),
          bgcolor: 'common.black',
          position: 'relative',
          cursor: 'pointer',
          overflow: 'hidden',
          // Only the outer card wrapper is rounded — the video itself fills
          // flush to the card edges (cover crops the 16:9 source to the
          // column's fixed height instead of leaving dead space).
          borderRadius: 0,
          '& .video-js': { width: '100%', height: '100%' },
          '& .vjs-tech, & video': { width: '100%', height: '100%', objectFit: 'cover' },
        }}
        onClick={handlePlayPauseClick}
      >
        {showCenterPlayPauseIcon && !isPlaying && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              pointerEvents: 'none',
              bgcolor: 'rgba(0,0,0,0.3)',
              borderRadius: '50%',
              p: 1.5,
              display: 'flex',
            }}
          >
            <PlayArrowIcon sx={{ fontSize: 64, color: 'common.white' }} />
          </Box>
        )}
        {showCenterPlayPauseIcon && isPlaying && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              pointerEvents: 'none',
              bgcolor: 'rgba(0,0,0,0.3)',
              borderRadius: '50%',
              p: 1.5,
              display: 'flex',
              opacity: 0,
              transition: 'opacity 0.2s ease-in-out',
            }}
          >
            <PauseIcon sx={{ fontSize: 64, color: 'common.white' }} />
          </Box>
        )}
      </Box>
      {showCameraTabs && artifacts.length > 1 && (
        <Stack direction="row" spacing={0} sx={{ borderTop: `1px solid ${DIVIDER}` }}>
          {artifacts.map((artifact, index) => (
            <React.Fragment key={artifact.id ?? index}>
              {index > 0 && <Divider flexItem orientation="vertical" />}
              <Button
                fullWidth
                color={activeCamera === index ? 'primary' : 'inherit'}
                sx={{ borderRadius: 0, fontSize: '0.8rem', textTransform: 'none', py: 0.5 }}
                variant={activeCamera === index ? 'contained' : 'text'}
                onClick={cameraTabClickHandlers[index]}
              >
                {`${T.CAMERA} ${index + 1}`}
              </Button>
            </React.Fragment>
          ))}
        </Stack>
      )}
    </Box>
  )
})

export { VisionVideoPlayer }
